import React, { Fragment, useEffect, useState } from 'react';
import {
	Routes,
	Route,
	useNavigate,
	useParams,
	Outlet,
	Navigate,
} from 'react-router-dom';
import moment from 'moment';
import { Menu, Transition } from '@headlessui/react';
import { FolderIcon } from '@heroicons/react/20/solid';
import {
	CheckCircleIcon,
	ChevronLeftIcon,
	ExclamationTriangleIcon,
	FingerPrintIcon,
	MinusCircleIcon,
	MinusIcon,
	PlusCircleIcon,
	PlusIcon,
	RectangleGroupIcon,
	Squares2X2Icon,
	XCircleIcon,
	XMarkIcon,
	QueueListIcon,
	MagnifyingGlassIcon,
	Bars3Icon,
	UsersIcon,
	UserGroupIcon,
	DocumentIcon,
	Cog6ToothIcon,
	MapPinIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import {
	archiveAsset,
	getAsset,
	getAssets,
	getLocation,
	getLocations,
	getTickets,
	updateAsset,
} from '../../../api';
import { getTeam } from '../../../api/company';
import { getAllPowerUsers } from '../../../api/power-users';
import { addToWatchlist } from '../../../api/watchlist';
import { getModuleCategories } from '../../../api/modules';

import useModal from '../../../hooks/useModal';
import usePrevious from '../../../hooks/usePrevious';
import useAsync from '../../../hooks/useAsync';

import LoadingSkeleton from '../../../components/Shell/LoadingSkeleton';
import { MainContent, PageHeading } from '../../../components/Shell/Content';

import {
	classNames,
	createDataTree,
	getUserFullName,
	objectAccess,
	pluralize,
} from '../../../components/helpers/functions';

import ProtectedRoute from '../../../components/helpers/ProtectedRoute';
import Table from '../../../components/DataTable/Table_old';
import Badge from '../../../components/Badge';
import { AvatarGenerated } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import {
	AttributeRow,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';
import Modal from '../../../components/Shell/Modal';

import { Base, Content, NoAssetsPage } from '../../../components/Shell/Content';

import Overview from './Overview';
import Documents from './Documents';
import Vendors from './Vendors';
import Contracts from './Contracts';
import Watchlist from './Watchlist';
import Settings from './Settings';

import ReactSelect from 'react-select';
import { getTags } from '../../../api/tags';
import Banner from '../../../components/Banner';

const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const commonColumns = [
	{
		title: 'Status',
		dataIndex: 'online',
		key: 'status',
		render: (online, data) => {
			const hasIssues = data?.tickets?.filter(
				(ticket) => ticket.active,
			)?.length;

			const isStable = online && !hasIssues;

			return isStable ? (
				<Badge status="success" noBackground>
					Stable
				</Badge>
			) : (
				<Badge status={hasIssues ? 'error' : 'default'} noBackground>
					{`${online ? 'Online' : 'Offline'}${
						hasIssues
							? ` with ${hasIssues} issue${hasIssues > 1 ? 's' : ''}`
							: ''
					}`}
				</Badge>
			);
		},
	},
	{
		title: 'Asset',
		dataIndex: 'asset',
		key: 'status',
		render: (id, row) => {
			return row ? (
				<>
					<div className="font-medium">
						{`${row?.manufacturerName || row?.manufacturer?.name} ${
							row?.modelName || row?.model?.name
						}`}
					</div>
					<div className="text-gray-400">{`S/N: ${row?.serialNumber}`}</div>
				</>
			) : (
				'--'
			);
		},
	},
	{
		title: 'Tag',
		dataIndex: 'tag',
		key: 'tag',
	},
	{
		title: 'Serial Number',
		dataIndex: 'serialNumber',
		key: 'serial-number',
	},
	{
		title: 'Location',
		dataIndex: 'location',
		key: 'location',
		render: (location) => location?.name || 'Not set',
	},
];

const LocationList = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	useEffect(() => {
		document.title = 'new title';
	}, []);
	const { value: locations, execute: refreshLocations } =
		useAsync(getLocations);
	const { value: location, execute: refreshLocation } = useAsync(getLocation, {
		defaultPayload: { id },
	});
	const { value: items, execute: refreshAssets } = useAsync(getAssets, {
		defaultPayload: { locationId: id },
	});

	useEffect(() => {
		refreshLocations();
		refreshLocation({ id });
		refreshAssets({ locationId: id });
	}, [id]);

	const locationChildren = locations?.rows?.filter(
		(location) => location.parent_id === id,
	);

	let pages = [];

	const getParentLocation = (id) => {
		if (!id && locations) {
			return;
		}

		const temp = locations?.rows?.find((o) => o.id === id);
		if (temp) {
			pages.push({
				name: temp.name,
				path: `/assets/all?location=${temp.id}`,
				current: !temp?.parent_id,
			});
			return temp?.parent_id && getParentLocation(temp?.parent_id);
		} else {
			return null;
		}
	};

	getParentLocation(location?.id);

	return (
		<Base>
			<PageHeading pages={pages.reverse()} divider={false} homePath="/assets">
				Assets
			</PageHeading>
			<Content>
				{locationChildren?.length ? (
					<ul
						role="list"
						className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3"
					>
						{locationChildren?.map((location) => (
							<li
								key={location.id}
								className="relative col-span-1 flex rounded-md shadow-sm cursor-pointer"
								onClick={() => navigate(`/assets/all?location=${location.id}`)}
							>
								<div
									className={
										'bg-red-400 flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md'
									}
								>
									<FolderIcon className="w-6 h-6 text-white" />
								</div>
								<div className="flex flex-1 items-center justify-between truncate rounded-r-md border-t border-r border-b border-gray-200 bg-white">
									<div className="flex-1 truncate px-6 py-4 text-sm">
										<a className="font-medium text-gray-900 hover:text-gray-600">
											{location.name}
										</a>
									</div>
									<div className="flex-shrink-0 mr-2">
										<ChevronRightIcon
											className="h-5 w-5 text-gray-400"
											aria-hidden="true"
										/>
									</div>
								</div>
							</li>
						))}
					</ul>
				) : null}

				<Table
					columns={commonColumns}
					dataSource={items}
					onRow={(row) => navigate(`/assets/${row.id}`)}
					actions={[
						{
							name: 'Report Issue',
							button: true,
							type: 'primary',
							onClick: (row) => navigate(`/tickets/create?assetId=${row.id}`),
						},
					]}
					emptyTableText="No items at this location"
				/>
			</Content>
		</Base>
	);
};

const InventoryList = () => {
	const navigate = useNavigate();
	const { value: items, status } = useAsync(getAssets, {
		defaultPayload: {
			missingLocation: true,
		},
	});

	const { value: locations } = useAsync(getLocations);

	const locationTree = createDataTree(locations?.rows);

	const finalLocationTree = locationTree;

	return (
		<Base>
			<PageHeading divider={false}>Assets</PageHeading>
			<Content loading={status === 'pending'} error={status === 'error'}>
				<ul
					role="list"
					className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3"
				>
					{finalLocationTree.map((location) => (
						<li
							key={location.id}
							className="relative col-span-1 flex rounded-md shadow-sm cursor-pointer"
							onClick={() => navigate(`/assets/all?location=${location.id}`)}
						>
							<div
								className={
									'bg-red-400 flex-shrink-0 flex items-center justify-center w-16 text-white text-sm font-medium rounded-l-md'
								}
							>
								<FolderIcon className="w-6 h-6 text-white" />
							</div>
							<div className="flex flex-1 items-center justify-between truncate rounded-r-md border-t border-r border-b border-gray-200 bg-white">
								<div className="flex-1 truncate px-6 py-4 text-sm">
									<a className="font-medium text-gray-900 hover:text-gray-600">
										{location.name}
									</a>
								</div>
								<div className="flex-shrink-0 mr-2">
									<ChevronRightIcon
										className="h-5 w-5 text-gray-400"
										aria-hidden="true"
									/>
								</div>
							</div>
						</li>
					))}
				</ul>

				<h2 class="mt-10 text-lg font-medium leading-6 text-gray-900">
					No location set
				</h2>

				<Table
					columns={commonColumns}
					dataSource={items}
					onRow={(row) => navigate(`/assets/${row.id}`)}
					emptyTableText="No items at this location"
				/>
			</Content>
		</Base>
	);
};

export default () => {
	const navigate = useNavigate();
	const { id, tab } = useParams();
	const { open, setClose } = useModal('assetModalForm');
	const {
		open: isNearbyAssetSliderOpen,
		setOpen: openNearbyAssetsSlider,
		setClose: closeNearbyAssetsSlider,
	} = useModal('nearbyAssets');

	const { setOpen: openTagModal } = useModal('addTagModal');
	const [search, setSearch] = useState('');

	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');

	const { open: contractOverviewOpen, setOpen: openContractOverview } =
		useModal('contractOverview');

	const { open: addEventOption, setOpen: onOpenAddEventModal } =
		useModal('eventAdd');

	const {
		value: item,
		execute: refreshItem,
		status,
	} = useAsync(getAsset, {
		immediate: false,
	});

	const { execute: onArchiveAsset } = useAsync(archiveAsset, {
		immediate: false,
	});

	const {
		execute: onUpdateAsset,
		status: isUpdatingAsset,
		error: hasUpdatingError,
	} = useAsync(updateAsset, {
		forceCallback: true,
		immediate: false,
		showNotification: { success: 'Asset has been updated', error: 'raw' },
	});

	const { value: documentCategories } = useAsync(getModuleCategories, {
		defaultPayload: { id: DOCUMENTS_MODULE_ID },
		silent: true,
	});

	const { value: assetTickets, execute: onRefreshTickets } = useAsync(
		getTickets,
		{
			defaultPayload: {
				assetId: id,
				status: ['incoming', 'inprogress'],
			},
			silent: true,
		},
	);

	const {
		value: allAssets,
		status: isLoadingAllAssets,
		execute: onRefreshAssets,
	} = useAsync(getAssets, { silent: true });

	useEffect(() => {
		if (id) {
			refreshItem({ id });
			onRefreshTickets({
				assetId: id,
				status: ['incoming', 'inprogress'],
			});

			if (open) {
				setClose();
			}
		}
	}, [id]);

	const prevAddEventOption = usePrevious(addEventOption);

	useEffect(() => {
		if (!addEventOption && prevAddEventOption) {
			refreshItem({ id }, null, { silent: true });
		}
	}, [addEventOption]);

	console.log('item: ', item);

	const activeContracts = item?.contracts?.filter(
		(contract) => contract?.active && moment().isBefore(contract?.endDate),
	);

	const groupedTickets = assetTickets?.rows?.reduce(
		(prev, cur) => {
			if (cur?.status === 'incoming') {
				prev.incoming.push(cur);
			} else if (cur?.status === 'inprogress') {
				prev.inprogress.push(cur);
			}

			return prev;
		},
		{ incoming: [], inprogress: [] },
	);

	const { incoming: incomingTickets = [], inprogress: inprogressTickets = [] } =
		groupedTickets || {};

	console.log('incomingTickets:', inprogressTickets);

	const banners = [
		...(incomingTickets?.length
			? [
					{
						message: (
							<>
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<ExclamationTriangleIcon
											className="h-6 w-6 text-orange-500"
											aria-hidden="true"
										/>
									</div>
									<div className="ml-3">
										<p
											className={classNames(
												'text-sm font-light',
												'text-orange-800',
											)}
										>
											<div>
												<>
													<span className="font-semibold">Caution:</span>
													<span className="font-normal pl-1.5">
														{`There ${pluralize(
															incomingTickets?.length,
															'is',
															'are',
														)} ${incomingTickets?.length} incoming ${pluralize(
															incomingTickets?.length,
															'ticket',
															'tickets',
														)} for this asset`}
													</span>
												</>
											</div>
										</p>
									</div>
								</div>
							</>
						),
						status: 'warning',
						onClick: () => navigate('/tickets/status/incoming'),
					},
				]
			: []),
		...(inprogressTickets?.length
			? [
					{
						message: (
							<>
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<ExclamationTriangleIcon
											className="h-6 w-6 text-red-500"
											aria-hidden="true"
										/>
									</div>
									<div className="ml-3">
										<p
											className={classNames(
												'text-sm font-light',
												'text-red-800',
											)}
										>
											<div>
												<>
													<span className="font-semibold">Warning:</span>
													<span className="font-normal pl-1.5">
														{`There ${pluralize(
															inprogressTickets?.length,
															'is',
															'are',
														)} ${inprogressTickets?.length} open ${pluralize(
															inprogressTickets?.length,
															'ticket',
															'tickets',
														)} for this asset`}
													</span>
												</>
											</div>
										</p>
									</div>
								</div>
							</>
						),
						status: 'error',
						onClick: () => navigate('/tickets/in-progress'),
					},
				]
			: []),
	];

	const renderAssetPage = (tab) =>
		({
			overview: (
				<Overview
					item={item}
					refreshAllAssets={onRefreshAssets}
					refreshItem={refreshItem}
				/>
			),
			documents: (
				<Documents
					item={item}
					refresh={refreshItem}
					categories={documentCategories}
				/>
			),
			vendors: <Vendors item={item} refresh={refreshItem} />,
			'power-users': <Watchlist item={item} refresh={refreshItem} />,
		})[tab];

	return (
		<>
			{status === 'pending' ? (
				<LoadingSkeleton />
			) : item ? (
				<div className="max-w-(--breakpoint-xl) flex mx-auto">
					<div className="p-8 py-12 flex-1">
						<div className="block">
							<PageHeading containerClassName={'pb-0 mb-0'} divider={false}>
								<div className="flex items-center">
									<div className="">
										<div className="flex items-center">
											<div className="flex items-center">
												<div
													className=" rounded-lg py-4 mr-4 cursor-pointer flex items-center text-sm text-gray-600 "
													onClick={() => navigate('/assets')}
												>
													<ChevronLeftIcon className="w-4 h-4 text-gray-500 mr-3 font-semibold" />{' '}
												</div>
											</div>
											{`${item?.manufacturerName || item?.manufacturer?.name} ${
												item?.modelName || item?.model?.name
											}`}
										</div>
									</div>
								</div>
							</PageHeading>
							{banners?.length ? (
								<div className="mb-6">
									{banners?.map(({ status, message, onClick, size = 'sm' }) => (
										<Banner status={status} onClick={onClick} size={size}>
											{message}
										</Banner>
									))}
								</div>
							) : null}

							<div className="hidden sm:block border-b border-gray-200">
								<div className="flex items-center justify-between">
									<nav
										className="-mb-px flex space-x-4 lg:space-x-8"
										aria-label="Tabs"
									>
										{[
											{ name: 'Overview', key: 'overview', icon: Bars3Icon },
											{
												name: 'Documents',
												key: 'documents',
												icon: DocumentIcon,
											},
											{
												name: 'Service Vendors',
												key: 'vendors',
												icon: UsersIcon,
											},
											{
												name: 'Power Users',
												key: 'power-users',
												icon: UserGroupIcon,
											},
										].map((overviewTab) => {
											const current =
												overviewTab.key === tab ||
												(!tab && overviewTab.name === 'Overview');
											const TabIcon = overviewTab?.icon;
											return (
												<div
													key={overviewTab.name}
													onClick={() =>
														navigate(`/assets/${id}/${overviewTab.key || ''}`)
													}
													className={classNames(
														current
															? 'border-indigo-500 text-indigo-600'
															: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
														'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-normal cursor-pointer items-center',
													)}
													aria-current={current ? 'page' : undefined}
												>
													{overviewTab?.icon ? (
														<TabIcon className="w-4 h-4 mr-2" />
													) : null}
													{overviewTab.name}
													{overviewTab.count ? (
														<span
															className={classNames(
																current
																	? 'bg-indigo-100 text-indigo-600'
																	: 'bg-gray-100 text-gray-900',
																'ml-3 hidden rounded-full py-0.5 px-2.5 text-xs font-medium md:inline-block',
															)}
														>
															{overviewTab.count}
														</span>
													) : null}
												</div>
											);
										})}
									</nav>
									<div className="flex items-center">
										<nav
											className="-mb-px flex space-x-4 lg:space-x-8 mr-2 md:mr-4"
											aria-label="Tabs"
										>
											{[
												{
													name: 'Manage',
													key: 'settings',
													icon: Cog6ToothIcon,
												},
												{
													name: 'Nearby Assets',
													key: 'nearby',
													icon: MapPinIcon,
													onClick: () =>
														isNearbyAssetSliderOpen
															? closeNearbyAssetsSlider()
															: openNearbyAssetsSlider(),
												},
											].map((overviewTab) => {
												const current =
													overviewTab.key === tab ||
													(!tab && overviewTab.name === 'Overview');
												const TabIcon = overviewTab?.icon;

												if (overviewTab?.key === 'settings') {
													return (
														<Menu
															as="div"
															className="relative inline-block text-left"
														>
															<div className="flex">
																<Menu.Button className="focus:outline-none">
																	<div
																		className={classNames(
																			false
																				? 'border-indigo-500 text-indigo-600'
																				: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
																			'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-normal cursor-pointer items-center',
																		)}
																	>
																		<div className="block xl:hidden flex items-center">
																			{overviewTab?.icon ? (
																				<TabIcon className="w-6 h-6" />
																			) : null}
																		</div>

																		<div className="hidden xl:flex items-center">
																			{overviewTab?.icon ? (
																				<TabIcon className="w-4 h-4 mr-2" />
																			) : null}
																			{overviewTab.name}
																		</div>
																	</div>
																</Menu.Button>
															</div>

															<Transition
																as={Fragment}
																enter="transition ease-out duration-100"
																enterFrom="transform opacity-0 scale-95"
																enterTo="transform opacity-100 scale-100"
																leave="transition ease-in duration-75"
																leaveFrom="transform opacity-100 scale-100"
																leaveTo="transform opacity-0 scale-95"
															>
																<Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
																	<div className="py-1">
																		<Menu.Item>
																			{({ active }) => (
																				<div
																					className={classNames(
																						active
																							? 'bg-gray-100 text-gray-900'
																							: 'text-gray-700',
																						'block px-4 py-2 text-xs cursor-pointer',
																					)}
																					onClick={() =>
																						navigate(
																							`/tickets/create?assetId=${item?.id}`,
																						)
																					}
																				>
																					Report Issue
																				</div>
																			)}
																		</Menu.Item>
																		<Menu.Item>
																			{({ active }) => (
																				<div
																					className={classNames(
																						'hover:bg-red-50 text-red-900 block px-4 py-2 text-xs cursor-pointer',
																					)}
																					onClick={() =>
																						openDeleteModal({
																							title:
																								'Are you sure you want to delete this asset?',
																							description:
																								'You will have 30 days to undo this asset.  You can access assets scheduled to be permanently from the App Settings page',
																							confirmText: 'Delete Asset',
																							onConfirm: () =>
																								onArchiveAsset(
																									{ id: item?.id },
																									() => {
																										closeDeleteModal();
																										navigate('/assets');
																									},
																								),
																						})
																					}
																				>
																					Delete
																				</div>
																			)}
																		</Menu.Item>
																	</div>
																</Menu.Items>
															</Transition>
														</Menu>
													);
												}
												return (
													<div
														key={overviewTab.name}
														onClick={() =>
															typeof overviewTab.onClick === 'function'
																? overviewTab?.onClick()
																: {}
														}
														className={classNames(
															current
																? 'border-indigo-500 text-indigo-600'
																: 'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700',
															'flex whitespace-nowrap border-b-2 py-4 px-1 text-sm font-normal cursor-pointer items-center',
														)}
														aria-current={current ? 'page' : undefined}
													>
														<div className="block xl:hidden flex items-center">
															{overviewTab?.icon ? (
																<TabIcon className="w-6 h-6 mr-2" />
															) : null}
														</div>

														<div className="hidden xl:flex items-center">
															{overviewTab?.icon ? (
																<TabIcon className="w-4 h-4 mr-2" />
															) : null}
															{overviewTab.name}
														</div>
													</div>
												);
											})}
										</nav>
									</div>
								</div>
							</div>
							{/*<div className="my-10">
								<WidgetDashboard />
								</div>*/}
							<div className="hidden sm:block">{renderAssetPage(tab)}</div>
							<div className="hidden sm:block">
								{/*<Routes>
									<ProtectedRoute
										exact
										path="/assets/:id"
										component={() => (
											<Overview
												item={item}
												refreshAllAssets={onRefreshAssets}
												refreshItem={refreshItem}
											/>
										)}
									/>
									<ProtectedRoute
										path="/assets/:id/documents"
										component={() => (
											<Documents
												item={item}
												refresh={refreshItem}
												categories={documentCategories}
											/>
										)}
									/>
									<ProtectedRoute
										exact
										path="/assets/:id/vendors"
										component={() => (
											<Vendors item={item} refresh={refreshItem} />
										)}
									/>
									<ProtectedRoute
										exact
										path="/assets/:id/contracts"
										component={() => (
											<Contracts item={item} refresh={refreshItem} />
										)}
									/>
									<ProtectedRoute
										exact
										path="/assets/:id/power-users"
										component={() => (
											<Watchlist item={item} refresh={refreshItem} />
										)}
									/>
									<ProtectedRoute
										exact
										path="/assets/:id/settings"
										component={() => (
											<Settings item={item} refresh={refreshItem} />
										)}
									/>
								</Routes>*/}
							</div>

							<div className="block sm:hidden">
								{renderAssetPage('overview')}
								{renderAssetPage('documents')}
								{renderAssetPage('vendors')}
								{renderAssetPage('power-users')}
							</div>
						</div>
					</div>

					{/*}
						<div className="sm:hidden">
							<PageHeading
								breadcrumbs={[
									{ name: 'All Assets', path: '/assets' },
									...(item?.location
										? [
												{
													name: item?.location?.name,
													path: `/assets/location/${item?.location?.id}`,
												},
										  ]
										: [
												{
													name: 'No location set',
													path: '/assets',
												},
										  ]),
								]}
							>
								<AvatarGenerated size="md" id={item?.id} type="asset" />

								<div className="mt-5 -mb-5">{`${
									item?.manufacturerName || item?.manufacturer?.name
								} ${item?.modelName || item?.manufacturer?.name}`}</div>
							</PageHeading>

							<dl
								className="grid grid-cols-2 gap-x-4 gap-y-8 pb-6
						"
							>
								{[
									{
										label: 'Manufacturer',
										value: item?.manufacturerName || item?.manufacturer?.name,
									},
									{
										label: 'Model',
										value: item?.modelName || item?.model?.name,
									},
									{
										label: 'Serial Number',
										value: item?.serialNumber,
									},
									{
										label: 'Tag',
										value: item?.tag,
									},
									{
										label: 'Location',
										value: item?.location?.name,
									},
								]
									.filter((field) => field.value)
									.map((field) => (
										<div className="sm:col-span-1">
											<dt className="text-xs font-medium text-gray-500">
												{field.label}
											</dt>
											<dd className="mt-1 text-sm text-gray-900">
												{field.value}
											</dd>
										</div>
									))}
							</dl>
							{item?.attributes?.length ? (
								<div className="flex justify-end">
									<Button
										type="secondary"
										onClick={() => setOpen({ id: item?.id })}
										Icon={InformationCircleIcon}
									>
										View more details
									</Button>
								</div>
							) : null}

							<div className="text-md my-3 font-medium tracking-tight text-gray-700 border-b pb-3">
								Overview
							</div>
							<OperationalChart item={item} />

							<Documents item={item} refresh={refreshItem} bare />
							<Vendors item={item} refresh={refreshItem} bare />
							<Watchlist item={item} refresh={refreshItem} />
							<Timeline item={item} bare />
							</div>*/}
				</div>
			) : (
				<>
					<NoAssetsPage />
				</>
			)}
		</>
	);
};
/*(

export default () => (
	<Routes>
		<Route index element={<Single />} />
		<Route path=":tab" element={<Single />} />
	</Routes>
);

	<Routes>
		<ProtectedRoute exact path="/assets" component={InventoryList} />
		<ProtectedRoute
			exact
			path="/assets/location/:id"
			component={LocationList}
		/>
		<ProtectedRoute exact path="/assets/:id" component={Single} />
		<ProtectedRoute exact path="/assets/:id/:tab" component={Single} />
		<ProtectedRoute exact path="/assets/:id/:tab/:setting" component={Single} />
	</Routes>
);
*/
